// Store Locator - Google Sheets Integration with Debug Mode
// Shows all stores on load, then filters by selected state/city + radius

let storesData = [];
let map;
let markers = [];
let userMarker = null;

// Google Sheets Configuration
const SHEET_ID = '1kHdn-B4jHrAyS2xdsN9TVNzsExtvxy5r-cQEf-DqlQQ';
const SHEET_NAME = 'Sheet1';

// Enable debug mode
const DEBUG = true;

function debugLog(message, data = null) {
  if (DEBUG) {
    console.log(`🔍 [STORE LOCATOR] ${message}`);
    if (data) console.log(data);
  }
}

// SVG Icons
const svgIcons = {
  showroom: `<svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>`,
  
  workshop: `<svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
  </svg>`,
  
  phone: `<svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>`,
  
  person: `<svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>`,
  
  location: `<svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>`
};

// Load data from Google Sheets
async function loadStoresFromGoogleSheets() {
  try {
    // The 'tqx=out:json' query allows us to get JSON back from the sheet
    const url = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=${encodeURIComponent(SHEET_NAME)}`;
    
    const response = await fetch(url);
    const text = await response.text();
    
    // Google returns a prefix "/*O_o*/ google.visualization.Query.setResponse(...);"
    // We need to strip that to get valid JSON
    const jsonString = text.substring(text.indexOf('{'), text.lastIndexOf('}') + 1);
    const rawData = JSON.parse(jsonString);
    console.log(rawData);
    
    // Map the Google Sheet rows to clean Objects
    storesData = parseSheetToObjects(rawData.table);
    
    console.log(`✅ Loaded ${storesData.length} stores from Google Sheets`);
    return true;
  } catch (error) {
    console.error('❌ Error loading sheet:', error);
    return false;
  }
}

function parseSheetToObjects(table) {
  const headers = table.cols.map(col => col.label || '');
  
  // Helper to find column index regardless of case or minor typos
  const findIdx = (names) => headers.findIndex(h => 
    names.some(name => h.toLowerCase().trim().includes(name.toLowerCase()))
  );

  const idx = {
    dealer: findIdx(['DEALERSHIP']),
    code: findIdx(['DEALER CODE']),
    address: findIdx(['Full Address', 'ADDRESS']),
    city: findIdx(['LOCATION']),
    state: findIdx(['STATE']),
    lat: findIdx(['Latitiude']), // Matching your specific header spelling
    lng: findIdx(['Longitude']),
    abm: findIdx(['Area Business Manager']),
    rsm: findIdx(['REGIONAL SALES MANAGER'])
  };

  return table.rows.map(row => {
    const c = row.c;
    return {
      DEALERSHIP: c[idx.dealer]?.v || '',
      'DEALER CODE': c[idx.code]?.v || '',
      'Full Address': c[idx.address]?.v || '',
      LOCATION: c[idx.city]?.v || '',
      STATE: c[idx.state]?.v || '',
      Lat: parseFloat(c[idx.lat]?.v),
      Long: parseFloat(c[idx.lng]?.v),
      ABM: c[idx.abm]?.v || '',
      RSM: c[idx.rsm]?.v || ''
    };
  }).filter(store => !isNaN(store.Lat) && store.STATE); // Only keep rows with coordinates and state
}

// Parse Google Sheets data
function parseGoogleSheetsData(data) {
  debugLog('Starting to parse Google Sheets data...');
  
  if (!data || !data.table || !data.table.rows) {
    console.error('Invalid data structure:', data);
    return [];
  }
  
  const rows = data.table.rows;
  const headers = data.table.cols.map(col => col.label || col.id || '');
  
  debugLog('📋 Column headers:', headers);
  debugLog('Total rows:', rows.length);
  
  // Find column indices (case-insensitive, flexible matching)
  const findColumn = (possibleNames) => {
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].toString().toUpperCase().trim();
      for (const name of possibleNames) {
        if (header.includes(name.toUpperCase())) {
          return i;
        }
      }
    }
    return -1;
  };
  
  const indices = {
    srNo: findColumn(['SR NO', 'SR.NO', 'SRNO', 'SR', 'Sr no']),
    typeOfSetup: findColumn(['TYPE OF SET UP', 'TYPE OF SETUP']),
    dealerCode: findColumn(['DEALER CODE', 'DEALERCODE', 'CODE']),
    dealership: findColumn(['DEALERSHIP', 'DEALER']),
    showroom: findColumn(['SHOWROOM']),
    fullAddress: findColumn(['FULL ADDRESS', 'Full Address']),
    location: findColumn(['LOCATION', 'CITY']),
    region: findColumn(['REGION']),
    state: findColumn(['STATE']),
    abm: findColumn(['ABM', 'AREA BUSINESS MANAGER', 'Area Business Manager']),
    rsm: findColumn(['RSM', 'REGIONAL SALES MANAGER']),
    dealerOnboarded: findColumn(['DEALER ONBOARDED', 'Dealer Onbored Yes or Not']),
    
    // Handle duplicate "Latitiud" columns
    lat: -1,
    long: -1
  };
  
  // Manually find Latitude and Longitude columns
  // Your sheet has TWO columns both named "Latitiud"
  // First one is Latitude, second one is Longitude
  let latFound = false;
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i].toString().trim();
    
    if (header === 'Latitiud' && !latFound) {
      indices.lat = i;
      latFound = true;
      debugLog(`✅ Found LATITUDE at column ${i}: "${header}"`);
    } else if (header === 'Latitiud' && latFound) {
      indices.long = i;
      debugLog(`✅ Found LONGITUDE at column ${i}: "${header}"`);
    }
  }
  
  debugLog('📍 Final column indices:', indices);
  
  const stores = [];
  let skippedRows = 0;
  
  rows.forEach((row, index) => {
    try {
      const cells = row.c || [];
      
      const getValue = (idx) => {
        if (idx === -1 || !cells[idx]) return '';
        const value = cells[idx].v;
        return value !== null && value !== undefined ? value.toString().trim() : '';
      };
      
      const srNo = getValue(indices.srNo);
      const typeOfSetup = getValue(indices.typeOfSetup);
      const dealerCode = getValue(indices.dealerCode);
      const dealership = getValue(indices.dealership);
      const showroom = getValue(indices.showroom);
      const fullAddress = getValue(indices.fullAddress);
      const location = getValue(indices.location);
      const region = getValue(indices.region);
      const state = getValue(indices.state);
      const abm = getValue(indices.abm);
      const rsm = getValue(indices.rsm);
      const dealerOnboarded = getValue(indices.dealerOnboarded);
      const lat = getValue(indices.lat);
      const long = getValue(indices.long);
      
      // Debug first few rows
      if (index < 3) {
        debugLog(`Row ${index} data:`, {
          state,
          location,
          lat,
          long,
          dealership
        });
      }
      
      // Only add if we have essential data
      if (state && location && lat && long) {
        const parsedLat = parseFloat(lat);
        const parsedLong = parseFloat(long);
        
        if (!isNaN(parsedLat) && !isNaN(parsedLong) && parsedLat !== 0 && parsedLong !== 0) {
          stores.push({
            'SR No': srNo,
            'TYPE OF SET UP': typeOfSetup,
            'DEALER CODE': dealerCode,
            'DEALERSHIP': dealership,
            'SHOWROOM': showroom,
            'Full Address': fullAddress,
            'LOCATION': location,
            'REGION': region,
            'STATE': state,
            'ABM': abm,
            'RSM': rsm,
            'Dealer Onboarded': dealerOnboarded,
            'Lat': parsedLat,
            'Long': parsedLong
          });
        } else {
          skippedRows++;
          if (index < 5) {
            debugLog(`Row ${index}: Invalid coordinates - Lat: ${lat} (${parsedLat}), Long: ${long} (${parsedLong})`);
          }
        }
      } else {
        skippedRows++;
        if (index < 5) {
          debugLog(`Row ${index}: Missing essential data - State: "${state}", Location: "${location}", Lat: "${lat}", Long: "${long}"`);
        }
      }
    } catch (error) {
      skippedRows++;
      console.warn(`⚠️ Error parsing row ${index}:`, error);
    }
  });
  
  debugLog(`✅ Parsed ${stores.length} valid stores, skipped ${skippedRows} rows`);
  
  if (stores.length > 0) {
    debugLog('First valid store:', stores[0]);
  }
  
  return stores;
}

// Initialize map
async function initMap() {
  debugLog('Initializing map...');

  const storesList = document.getElementById('stores-list');
  if (!storesList) {
    console.error('❌ stores-list element not found!');
    return;
  }

  const isDesktop = window.matchMedia('(min-width: 768px)').matches;

  // Loading state - full width centered message (desktop only)
  if (isDesktop) {
    storesList.style.gridTemplateColumns = '1fr';
    storesList.style.placeItems = 'center';
  }

  storesList.innerHTML =
    '<div class="loading">📡 Loading dealer data from Google Sheets...</div>';

  try {
    map = L.map('map').setView([20.5937, 78.9629], 5);
    debugLog('Map object created');

    // L.tileLayer('https://api.maptiler.com/maps/hybrid-v4/{z}/{x}/{y}.jpg?key=GtuiNnyidIzfABMVNiO8', {
    //   attribution: '&copy; MapTiler & OpenStreetMap',
    //   maxZoom: 19
    // }).addTo(map);

    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: 'Tiles © Esri',
      }
    ).addTo(map);

    debugLog('Tile layer added');
  } catch (error) {
    console.error('❌ Error creating map:', error);

    storesList.innerHTML =
      '<div class="no-results">❌ Failed to initialize map.</div>';

    return;
  }

  const success = await loadStoresFromGoogleSheets();

  if (!success || storesData.length === 0) {
    storesList.innerHTML = `
      <div class="no-results">
        <p>❌ No dealer data loaded.</p>
        <p style="margin-top: 10px; font-size: 13px;">
          Please check console for details.
        </p>
        <button
          onclick="location.reload()"
          style="margin-top: 10px; padding: 10px 20px; background: #c00; color: white; border: none; cursor: pointer;"
        >
          Retry
        </button>
      </div>
    `;
    return;
  }

  // Restore normal layout after loading (desktop only)
  if (isDesktop) {
    storesList.style.gridTemplateColumns = 'repeat(3, 1fr)';
    storesList.style.removeProperty('place-items');
  }

  populateStateDropdown();
  displayAllStores();
}

// Get unique states
function getUniqueStates() {
  const states = [...new Set(storesData.map(store => store.STATE))];
  return states.filter(state => state).sort();
}

// Get cities for state
function getCitiesForState(stateName) {
  const cities = storesData
    .filter(store => store.STATE === stateName && store.LOCATION)
    .map(store => store.LOCATION);
  return [...new Set(cities)].sort();
}

// Populate state dropdown
function populateStateDropdown() {
  const stateSelect = document.getElementById('state-select');
  if (!stateSelect) return;
  
  const states = getUniqueStates();
  stateSelect.innerHTML = '<option value="">Select State</option>';
  
  states.forEach(state => {
    const option = document.createElement('option');
    option.value = state;
    option.textContent = state;
    stateSelect.appendChild(option);
  });
  
  debugLog(`✅ Populated ${states.length} states`);
}

// Populate city dropdown
function populateCityDropdown(stateName) {
  const citySelect = document.getElementById('city-select');
  if (!citySelect) return;
  
  if (!stateName) {
    citySelect.innerHTML = '<option value="">Select State First</option>';
    citySelect.disabled = true;
    return;
  }
  
  const cities = getCitiesForState(stateName);
  citySelect.innerHTML = '<option value="">Select City</option>';
  
  cities.forEach(city => {
    const option = document.createElement('option');
    option.value = city;
    option.textContent = city;
    citySelect.appendChild(option);
  });
  
  citySelect.disabled = false;
  debugLog(`✅ Populated ${cities.length} cities for ${stateName}`);
}

// Calculate distance
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Display all stores
function displayAllStores() {
  debugLog('Displaying all stores...');
  
  clearMarkers();
  const storesList = document.getElementById('stores-list');
  storesList.innerHTML = '';

  if (!storesData || storesData.length === 0) {
    storesList.innerHTML = '<div class="no-results">No stores available.</div>';
    return;
  }

  const bounds = [];

  storesData.forEach((store, index) => {
    const lat = parseFloat(store.Lat);
    const lng = parseFloat(store.Long);
    
    if (isNaN(lat) || isNaN(lng)) return;

    bounds.push([lat, lng]);

    const markerIcon = L.divIcon({
      className: 'custom-marker-wrapper',
      html: `<div class="custom-red-marker"></div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });

    const marker = L.marker([lat, lng], { icon: markerIcon }).addTo(map);
    
    const popupContent = `
      <div style="min-width: 250px;">
        <h3 style="margin: 0 0 8px 0; color: #c00; font-size: 16px;">${store.DEALERSHIP || 'N/A'}</h3>
        <p style="margin: 4px 0; font-size: 13px;"><strong>Code:</strong> ${store['DEALER CODE'] || 'N/A'}</p>
        <p style="margin: 4px 0; font-size: 13px;"><strong>Location:</strong> ${store.LOCATION || 'N/A'}</p>
        <p style="margin: 4px 0; font-size: 13px;"><strong>State:</strong> ${store.STATE || 'N/A'}</p>
        ${store.REGION ? `<p style="margin: 4px 0; font-size: 13px;"><strong>Region:</strong> ${store.REGION}</p>` : ''}
        ${store.SHOWROOM ? `<p style="margin: 4px 0; font-size: 13px;"><strong>Showroom:</strong> ${store.SHOWROOM}</p>` : ''}
        ${store['Full Address'] ? `<p style="margin: 4px 0; font-size: 13px;"><strong>Address:</strong> ${store['Full Address']}</p>` : ''}
      </div>
    `;
    
    marker.bindPopup(popupContent);
    marker.on('click', () => highlightStore(index));
    markers.push(marker);

    const storeElement = createStoreElement(store, index);
    storesList.appendChild(storeElement);
  });

  if (bounds.length > 0) {
    map.fitBounds(bounds, { padding: [50, 50] });
  }
  
  debugLog(`✅ Displayed ${storesData.length} stores`);
}

// Create store element
function createStoreElement(store, index, distance = null) {
  const div = document.createElement('div');
  div.className = 'store-item';
  div.dataset.index = index;

  div.innerHTML = `
    <div class="store-name">${store.DEALERSHIP || 'N/A'}</div>
    ${store['DEALER CODE'] ? `<div class="store-details" style="font-weight: 600; color: #666;">Code: ${store['DEALER CODE']}</div>` : ''}
    <div class="store-location">${store.LOCATION || 'N/A'}</div>
    <div class="store-details">${store.STATE || 'N/A'}</div>
    ${store.REGION ? `<div class="store-details">Region: ${store.REGION}</div>` : ''}
    ${store.SHOWROOM ? `<div class="store-details">${svgIcons.showroom} ${store.SHOWROOM}</div>` : ''}
    ${store['Full Address'] ? `<div class="store-details">${svgIcons.location} ${store['Full Address']}</div>` : ''}
    ${distance !== null ? `<div class="store-distance">${svgIcons.location} ${distance.toFixed(1)} km away</div>` : ''}
    <button class="get-directions-btn" data-lat="${store.Lat}" data-lng="${store.Long}">
      Get Directions →
    </button>
  `;

  div.querySelector('.get-directions-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    getDirections(e.target.dataset.lat, e.target.dataset.lng);
  });

  div.addEventListener('click', () => {
    highlightStore(index);
    map.setView([parseFloat(store.Lat), parseFloat(store.Long)], 14);
    markers[index].openPopup();
  });

  return div;
}

// Highlight store
function highlightStore(index) {
  document.querySelectorAll('.store-item').forEach(item => {
    item.classList.remove('active');
  });
  const selected = document.querySelector(`.store-item[data-index="${index}"]`);
  if (selected) {
    selected.classList.add('active');
    selected.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

// Clear markers
function clearMarkers() {
  markers.forEach(marker => map.removeLayer(marker));
  markers = [];
  if (userMarker) {
    map.removeLayer(userMarker);
    userMarker = null;
  }
}

// Get directions
function getDirections(lat, lng) {
  window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
}

// Search stores
function searchStores() {
  const selectedState = document.getElementById('state-select').value;
  const selectedCity = document.getElementById('city-select').value;
  const radius = parseFloat(document.getElementById('search-radius').value);

  if (!selectedState) {
    alert('Please select a state');
    return;
  }

  if (!selectedCity) {
    alert('Please select a city');
    return;
  }

  const referenceStore = storesData.find(
    store => store.STATE === selectedState && store.LOCATION === selectedCity
  );

  if (!referenceStore) {
    alert('No store found for the selected location');
    return;
  }

  const userLat = parseFloat(referenceStore.Lat);
  const userLng = parseFloat(referenceStore.Long);

  filterStoresByRadius(userLat, userLng, radius, selectedCity, selectedState);
}

// Filter by radius
function filterStoresByRadius(userLat, userLng, radius, cityName, stateName) {
  clearMarkers();
  const list = document.getElementById('stores-list');
  list.innerHTML = '';

  const filtered = storesData
    .map(store => ({
      ...store,
      distance: calculateDistance(userLat, userLng, parseFloat(store.Lat), parseFloat(store.Long))
    }))
    .filter(store => !isNaN(store.distance) && store.distance <= radius)
    .sort((a, b) => a.distance - b.distance);

  if (filtered.length === 0) {
    list.innerHTML = `
      <div class="no-results">
        <p>❌ No dealers within ${radius} km of</p>
        <p style="font-weight: bold; color: #c00;">${cityName}, ${stateName}</p>
        <p style="margin-top: 10px; font-size: 13px;">Try increasing the radius</p>
      </div>
    `;
    
    const userIcon = L.divIcon({
      className: 'user-marker-wrapper',
      html: `<div style="width: 20px; height: 20px; background: #4285f4; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(66, 133, 244, 0.4);"></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });
    userMarker = L.marker([userLat, userLng], { icon: userIcon }).addTo(map);
    userMarker.bindPopup(`<strong>📍 ${cityName}, ${stateName}</strong>`);
    map.setView([userLat, userLng], 10);
    return;
  }

 const info = document.createElement('div');
  info.className = 'search-info full-width';

  info.innerHTML = `✅ Found <strong>${filtered.length}</strong> dealer${filtered.length > 1 ? 's' : ''} within <strong>${radius} km</strong> of <strong>${cityName}, ${stateName}</strong>`;

  list.appendChild(info);

  const bounds = [];

  const userIcon = L.divIcon({
    className: 'user-marker-wrapper',
    html: `<div style="width: 20px; height: 20px; background: #4285f4; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(66, 133, 244, 0.4);"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
  
  userMarker = L.marker([userLat, userLng], { icon: userIcon }).addTo(map);
  userMarker.bindPopup(`<strong>📍 ${cityName}, ${stateName}</strong>`);
  bounds.push([userLat, userLng]);

  filtered.forEach((store, index) => {
    const lat = parseFloat(store.Lat);
    const lng = parseFloat(store.Long);
    
    if (isNaN(lat) || isNaN(lng)) return;

    bounds.push([lat, lng]);

    const icon = L.divIcon({
      className: 'custom-marker-wrapper',
      html: `<div class="numbered-red-marker"><span>${index + 1}</span></div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 36],
      popupAnchor: [0, -36]
    });

    const marker = L.marker([lat, lng], { icon }).addTo(map);
    marker.bindPopup(`
      <div style="min-width: 250px;">
        <h3 style="margin: 0 0 8px 0; color: #c00; font-size: 16px;">${index + 1}. ${store.DEALERSHIP}</h3>
        <p style="margin: 4px 0; font-size: 13px;"><strong>Distance:</strong> ${store.distance.toFixed(1)} km</p>
        <p style="margin: 4px 0; font-size: 13px;"><strong>Code:</strong> ${store['DEALER CODE'] || 'N/A'}</p>
        <p style="margin: 4px 0; font-size: 13px;"><strong>Location:</strong> ${store.LOCATION}</p>
        <p style="margin: 4px 0; font-size: 13px;"><strong>State:</strong> ${store.STATE}</p>
      </div>
    `);
    marker.on('click', () => highlightStore(index));
    markers.push(marker);

    list.appendChild(createStoreElement(store, index, store.distance));
  });

  if (bounds.length > 0) {
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
  }
}

// Clear search
function clearSearch() {
  document.getElementById('state-select').value = '';
  document.getElementById('city-select').value = '';
  document.getElementById('city-select').disabled = true;
  document.getElementById('city-select').innerHTML = '<option value="">Select State First</option>';
  document.getElementById('search-radius').value = '50';
  displayAllStores();
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  debugLog('DOM Content Loaded');
  
  const requiredElements = ['map', 'stores-list', 'state-select', 'city-select', 'search-radius', 'search-btn', 'clear-search'];
  const missing = requiredElements.filter(id => !document.getElementById(id));
  
  if (missing.length > 0) {
    console.error('❌ Missing elements:', missing);
    return;
  }
  
  debugLog('All required elements found');
  
  initMap();

  document.getElementById('state-select').addEventListener('change', (e) => {
    populateCityDropdown(e.target.value);
  });

  document.getElementById('search-btn').addEventListener('click', searchStores);
  document.getElementById('clear-search').addEventListener('click', clearSearch);
  
  debugLog('Event listeners attached');
});